import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, ChevronRight, Settings, Users, Building2, 
  Loader2, ExternalLink, AlertCircle, CheckCircle2, Edit3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

const FORM_SECTIONS = [
  {
    id: 'terms',
    title: 'Términos y Condiciones',
    description: 'Documentos legales y políticas de la plataforma',
    icon: FileText,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    items: [
      {
        id: 'terms-general',
        name: 'Términos Generales',
        description: 'Términos y condiciones de uso de la plataforma',
        endpoint: '/settings/terms',
        editPath: '/admin/terminos'
      },
      {
        id: 'terms-privacy',
        name: 'Sobre Nosotros / Quiénes Somos',
        description: 'Información sobre la empresa',
        endpoint: '/settings/about',
        editPath: '/admin/quienes-somos'
      },
      {
        id: 'terms-producer',
        name: 'Términos para Productoras',
        description: 'Condiciones para productoras al crear eventos',
        endpoint: '/settings/producer-terms',
        editPath: '/admin/terminos-productoras'
      }
    ]
  },
  {
    id: 'forms',
    title: 'Formularios de Registro',
    description: 'Formularios públicos de solicitud y registro',
    icon: Edit3,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    items: [
      {
        id: 'form-partner',
        name: 'Solicitud de Partner',
        description: 'Formulario de aplicación para nuevos partners',
        publicPath: '/quiero-ser-partner',
        editPath: '/admin/formulario-partner',
        configPath: '/admin/partners'
      },
      {
        id: 'form-producer',
        name: 'Solicitud de Productora',
        description: 'Formulario de aplicación para nuevas productoras',
        publicPath: '/productoras/aplicar',
        editPath: '/admin/formulario-productora',
        configPath: '/admin/productoras'
      },
      {
        id: 'form-press',
        name: 'Solicitud de Prensa',
        description: 'Formulario de aplicación para medios de prensa',
        publicPath: '/prensa/solicitud',
        editPath: '/admin/formulario-prensa',
        configPath: '/admin/prensa'
      },
      {
        id: 'verification-requirements',
        name: 'Verificación de Productoras',
        description: 'Requisitos y solicitudes de verificación',
        editPath: '/admin/verificacion-productoras'
      }
    ]
  },
  {
    id: 'content',
    title: 'Contenido Editable',
    description: 'Páginas y secciones con contenido modificable',
    icon: Settings,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    items: [
      {
        id: 'content-gallery',
        name: 'Galería',
        description: 'Gestión de imágenes y fotos',
        editPath: '/admin/galeria'
      },
      {
        id: 'content-auras',
        name: 'Auras',
        description: 'Gestión de efectos visuales de auras',
        editPath: '/admin/auras'
      },
      {
        id: 'content-fullbravx',
        name: 'Suscripciones FULL BRAVX',
        description: 'Gestión de planes de suscripción',
        editPath: '/admin/suscripciones'
      }
    ]
  }
];

const FormsAdminPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState({});

  useEffect(() => {
    checkFormStatus();
  }, []);

  const checkFormStatus = async () => {
    try {
      // Check status of various forms/content
      const checks = await Promise.allSettled([
        api.get('/settings/terms'),
        api.get('/settings/producer-terms'),
        api.get('/settings/about')
      ]);

      const status = {};
      
      // Terms general
      if (checks[0].status === 'fulfilled') {
        status['terms-general'] = { 
          exists: true, 
          lastUpdated: checks[0].value?.data?.last_updated 
        };
      }
      
      // Terms producer
      if (checks[1].status === 'fulfilled') {
        status['terms-producer'] = { 
          exists: true, 
          lastUpdated: checks[1].value?.data?.last_updated 
        };
      }
      
      // About
      if (checks[2].status === 'fulfilled') {
        status['content-about'] = { 
          exists: true, 
          lastUpdated: checks[2].value?.data?.last_updated 
        };
      }

      setFormStatus(status);
    } catch (error) {
      console.error('Error checking form status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 lg:p-8"
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
              <FileText className="text-[#D4AF37]" />
              Formularios y Contenido
            </h1>
            <p className="text-zinc-400 mt-1">
              Gestiona todos los formularios, términos y contenido editable de la plataforma
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {FORM_SECTIONS.map((section, sectionIndex) => {
              const SectionIcon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: sectionIndex * 0.1 }}
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${section.bgColor}`}>
                      <SectionIcon className={section.color} size={20} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">{section.title}</h2>
                      <p className="text-sm text-zinc-500">{section.description}</p>
                    </div>
                  </div>

                  {/* Items Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {section.items.map((item) => {
                      const status = formStatus[item.id];
                      const editPath = item.editPath || item.configPath;
                      
                      return (
                        <div
                          key={item.id}
                          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-white">{item.name}</h3>
                              <p className="text-sm text-zinc-500 mt-1">{item.description}</p>
                            </div>
                            {status?.exists && (
                              <CheckCircle2 className="text-green-500 flex-shrink-0" size={18} />
                            )}
                          </div>
                          
                          {status?.lastUpdated && (
                            <p className="text-xs text-zinc-600 mb-3">
                              Última actualización: {status.lastUpdated}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-3">
                            {editPath && (
                              <Link
                                to={editPath}
                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg text-sm font-medium hover:bg-[#D4AF37]/30 transition-colors"
                              >
                                <Edit3 size={14} />
                                Editar
                              </Link>
                            )}
                            {item.publicPath && (
                              <Link
                                to={item.publicPath}
                                target="_blank"
                                className="flex items-center justify-center gap-2 px-3 py-2 bg-zinc-800 text-zinc-400 rounded-lg text-sm hover:bg-zinc-700 hover:text-white transition-colors"
                              >
                                <ExternalLink size={14} />
                                Ver
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Info Banner */}
          <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-white font-medium">Nota sobre los formularios</p>
              <p className="text-sm text-zinc-400 mt-1">
                Los formularios de solicitud (Partner y Productora) no son editables directamente. 
                Para modificar los campos, contacta al equipo de desarrollo. Los términos y contenido 
                sí pueden editarse desde esta sección.
              </p>
            </div>
          </div>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default FormsAdminPage;
